import { hasCountryFlag, toIsoCode } from "@/app/(app)/_lib/iso-countries";

const TWEMOJI_BASE = "https://cdn.jsdelivr.net/gh/jdecked/twemoji@latest/assets/svg";

const A_CHAR = 65;
const REGIONAL_INDICATOR_BASE = 0x1f1e6;

function isoToCodepoint(iso: string): string | null {
  if (!iso || iso.length !== 2) return null;
  const upper = iso.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return null;
  // Legacy codes the backend still ships (e.g. AN) have no asset — a <img> for
  // them would 404, so render nothing rather than a broken image.
  if (!hasCountryFlag(upper)) return null;
  return [...upper]
    .map((c) => (c.charCodeAt(0) - A_CHAR + REGIONAL_INDICATOR_BASE).toString(16))
    .join("-");
}

export type FlagDesaturate = false | "soft" | "strong" | "grayscale";

const DESATURATE_FILTER: Record<Exclude<FlagDesaturate, false>, string> = {
  soft: "saturate(0.6)",
  strong: "saturate(0.3) brightness(1.05)",
  grayscale: "grayscale(1)",
};

type FlagProps = {
  /** Two-letter ISO 3166-1 alpha-2 code, e.g. "DE". */
  iso?: string;
  /** Country name; resolved to ISO via `toIsoCode`. Ignored if `iso` is set. */
  country?: string;
  /** Pixel size (square). Defaults to 16. */
  size?: number;
  /** Visual saturation. Defaults to "soft". Pass `false` for full color. */
  desaturate?: FlagDesaturate;
  className?: string;
  title?: string;
};

/**
 * Renders a country flag from the maintained Twemoji asset CDN.
 * Returns null when neither prop resolves to a valid ISO-2 code.
 */
export function Flag({
  iso,
  country,
  size = 16,
  desaturate = "soft",
  className = "",
  title,
}: FlagProps) {
  const code = iso ?? (country ? toIsoCode(country) || undefined : undefined);
  const cp = code ? isoToCodepoint(code) : null;
  if (!cp) return null;
  return (
    <img
      src={`${TWEMOJI_BASE}/${cp}.svg`}
      alt=""
      title={title ?? code ?? undefined}
      width={size}
      height={size}
      draggable={false}
      loading="lazy"
      style={desaturate ? { filter: DESATURATE_FILTER[desaturate] } : undefined}
      className={`inline-block shrink-0 select-none ${className}`}
    />
  );
}
